import React, { useState, useEffect } from 'react';
import { loucaApi } from '../../services/loucaApi';

/**
 * Liste des inspections en cours avec statut en temps réel
 */
const InspectionsList = ({ onSelectInspection, selectedSessionId }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const loadSessions = async () => {
    try {
      const response = await loucaApi.listSessionsV3();
      if (response.status === 'success' && response.sessions) {
        // Trier par date de création (plus récent en premier)
        const sorted = response.sessions.sort((a, b) => {
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateB - dateA;
        });
        setSessions(sorted);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadSessions();
    }, 3000); // Refresh every 3 seconds

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const getStatusBadge = (status) => {
    const badges = {
      created: { color: 'bg-blue-500/20 text-blue-400', label: 'Créée' },
      processing: { color: 'bg-orange-500/20 text-orange-400', label: 'En cours' },
      completed: { color: 'bg-green-500/20 text-green-400', label: 'Terminée' },
      failed: { color: 'bg-red-500/20 text-red-400', label: 'Échouée' },
    };
    const badge = badges[status] || badges.created;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR');
  };

  if (loading && sessions.length === 0) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-slate-700 rounded w-1/4"></div>
          <div className="h-20 bg-slate-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Inspections</h2>
          <p className="text-sm text-slate-400">
            {sessions.length} session(s) trouvée(s)
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadSessions}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
          >
            🔄 Actualiser
          </button>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
            <span className="ml-3 text-sm text-slate-300">Auto</span>
          </label>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-400">Erreur: {error}</p>
        </div>
      )}

      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {sessions.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            Aucune inspection trouvée
          </div>
        ) : (
          sessions.map((session) => (
            <button
              key={session.session_id}
              onClick={() => onSelectInspection(session.session_id)}
              className={`w-full text-left p-4 rounded-xl border transition ${
                selectedSessionId === session.session_id
                  ? 'bg-orange-500/10 border-orange-500/50'
                  : 'bg-slate-900/60 border-slate-700 hover:bg-slate-900'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm text-orange-300">
                    {session.session_id}
                  </span>
                  {getStatusBadge(session.status)}
                </div>
                <span className="text-xs text-slate-400">
                  {formatDate(session.created_at)}
                </span>
              </div>
              {session.config && (
                <div className="flex gap-2 text-xs text-slate-400">
                  <span>Step1: {session.config.v9_step1_model || 'N/A'}</span>
                  <span>•</span>
                  <span>Step2: {session.config.v9_step2_model || 'N/A'}</span>
                </div>
              )}
              {session.chunks && session.chunks.length > 0 && (
                <div className="mt-2 text-xs text-slate-400">
                  {session.chunks.length} chunk(s) uploadé(s)
                </div>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default InspectionsList;



